/**
 * Agent module helpers — context key parsing, timeline construction,
 * and context panel building.
 */

import type { IconName } from "../../components/ui/Icon";
import type { AgentChat, AgentModuleData, LinkedEntitySummary } from "./types";
import type { AvatarColor } from "../shared/types";
import type { ContextPanelProps } from "../../agent/ContextPanel";

// ─── Chat Timeline ───────────────────────────────────────────────────

export interface TimelineItem {
  readonly id: string;
  readonly title: string;
  readonly preview: string;
  readonly time: string;
  readonly activityAt: Date | null;
  readonly iconName: IconName;
  readonly color: AvatarColor;
  readonly status?: string;
  readonly sourceModuleId: string;
  readonly targetModuleId: string;
  readonly targetItemId?: string;
}

const SOURCE_VISUALS: Readonly<Record<string, { readonly icon: IconName; readonly color: AvatarColor }>> = {
  agent: { icon: "message-circle", color: "orange" },
  telegram: { icon: "send", color: "blue" },
  email: { icon: "mail", color: "pink" },
  file: { icon: "folder", color: "blue" },
  contacts: { icon: "contacts", color: "purple" },
  companies: { icon: "building", color: "green" },
  meetings: { icon: "calendar", color: "orange" },
  projects: { icon: "briefcase", color: "blue" },
  web: { icon: "link", color: "blue" },
  triggers: { icon: "zap", color: "orange" },
  episodes: { icon: "message-circle", color: "orange" },
  search: { icon: "search", color: "green" },
};

export function getSourceVisual(moduleId: string): { readonly icon: IconName; readonly color: AvatarColor } {
  return SOURCE_VISUALS[moduleId] ?? { icon: "message-circle", color: "blue" };
}

function parseRelativeTimeToEpoch(value: string, now: Date): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const exact = Date.parse(trimmed);
  if (!Number.isNaN(exact)) return exact;

  const agoMatch = /^(\d+)\s*([mhd])\s+ago$/i.exec(trimmed);
  if (agoMatch) {
    const amount = Number(agoMatch[1]);
    const unit = agoMatch[2]?.toLowerCase();
    if (unit === "m") return now.getTime() - amount * 60_000;
    if (unit === "h") return now.getTime() - amount * 3_600_000;
    if (unit === "d") return now.getTime() - amount * 86_400_000;
  }

  const weeksMatch = /^(\d+)\s+weeks?$/i.exec(trimmed);
  if (weeksMatch) {
    return now.getTime() - Number(weeksMatch[1]) * 7 * 86_400_000;
  }

  const clockMatch = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(trimmed);
  if (clockMatch) {
    const hours = Number(clockMatch[1]) % 12 + (clockMatch[3]?.toUpperCase() === "PM" ? 12 : 0);
    const minutes = Number(clockMatch[2]);
    const d = new Date(now);
    d.setHours(hours, minutes, 0, 0);
    return d.getTime();
  }

  const normalized = trimmed.toLowerCase();
  if (normalized === "today") return now.getTime() - 12 * 3_600_000;
  if (normalized === "yesterday") return now.getTime() - 24 * 3_600_000;
  if (normalized === "last week") return now.getTime() - 7 * 24 * 3_600_000;
  if (normalized === "previous 7 days") return now.getTime() - 7 * 24 * 3_600_000;

  const weekdayIndex: Readonly<Record<string, number>> = {
    sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6,
  };
  const shortDay = normalized.slice(0, 3);
  if (shortDay in weekdayIndex) {
    const day = weekdayIndex[shortDay] ?? now.getDay();
    const diff = (now.getDay() - day + 7) % 7 || 7;
    return now.getTime() - diff * 24 * 3_600_000;
  }

  return null;
}

function fromAgentChat(chat: AgentChat): TimelineItem {
  const visuals = getSourceVisual("episodes");
  return {
    id: `agent:${chat.id}`,
    title: chat.title,
    preview: chat.preview,
    time: chat.time,
    activityAt: chat.activityAt ? new Date(chat.activityAt) : null,
    iconName: visuals.icon,
    color: visuals.color,
    status: chat.status,
    sourceModuleId: "episodes",
    targetModuleId: "episodes",
    targetItemId: chat.id,
  };
}

function isUserInitiatedChat(chat: AgentChat): boolean {
  return chat.title.trim() !== "" || chat.preview.trim() !== "";
}

function buildTimelineFromChats(chats: readonly AgentChat[]): readonly TimelineItem[] {
  const now = new Date();
  const rawItems: TimelineItem[] = [
    ...chats.filter(isUserInitiatedChat).map(fromAgentChat),
  ];

  return rawItems
    .map((item, index) => {
      const parsed = parseRelativeTimeToEpoch(item.time, now);
      return {
        item: {
          ...item,
          activityAt: item.activityAt ?? (parsed != null ? new Date(parsed) : null),
        },
        order: index,
        sortEpoch: item.activityAt?.getTime() ?? parsed ?? now.getTime() - index * 1_000,
      };
    })
    .sort((a, b) => b.sortEpoch - a.sortEpoch || a.order - b.order)
    .map((entry) => entry.item);
}

export function buildUnifiedTimeline(data: AgentModuleData): readonly TimelineItem[] {
  const sourceChats = data.episodes && data.episodes.length > 0 ? data.episodes : data.chats;
  return buildTimelineFromChats(sourceChats);
}

// ─── "Needs reply" scope ─────────────────────────────────────────────

/** Chats list pane filter — "needs_reply" shows only episodes where the
 *  agent explicitly waits on the user; "all" is the default behavior. */
export type ChatListFilter = "all" | "needs_reply";

/** Single source of truth for "needs reply". `needs_input` is the ONLY
 *  status where the agent waits on the user (ask_user / pending tool
 *  approval). The former Inbox also counted `active` — a historical bug
 *  (`active` means it's the agent's turn). `idle` is deferred until
 *  unread-tracking exists. See docs/backend/episode-status.md. */
export const NEEDS_REPLY_STATUSES = ["needs_input"] as const;

// @tested-by: tst_fe_chats_filter_001
// @invariant: the needs-reply set is exactly {needs_input}
export function isNeedsReplyStatus(status: string | undefined): boolean {
  return status != null && (NEEDS_REPLY_STATUSES as readonly string[]).includes(status);
}

// @tested-by: tst_fe_chats_filter_002, tst_fe_chats_filter_003
// @invariant: "all" leaves the timeline untouched; "needs_reply" keeps
// only items in the needs-reply status set
export function filterChatTimeline(
  items: readonly TimelineItem[],
  filter: ChatListFilter,
): readonly TimelineItem[] {
  if (filter === "all") return items;
  return items.filter((item) => isNeedsReplyStatus(item.status));
}

/** One dropdown/badge item for the global command-bar bell. */
export interface NeedsReplyEpisode {
  readonly id: string;
  readonly title: string;
  readonly activityAt: string;
}

interface NeedsReplySource {
  readonly id: string;
  readonly title: string;
  readonly status?: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly date?: string;
  readonly last_message_at?: string;
}

// @tested-by: tst_fe_chats_filter_008, tst_fe_chats_filter_009
// @invariant: the global bell badge AND its dropdown items derive from
// this single needs_input-only selection (activity = date ??
// last_message_at ?? updated_at, same derivation as the Chats list),
// sorted recent-first — it can never disagree with the Chats-pane bell,
// which filters by the same isNeedsReplyStatus
export function selectNeedsReplyEpisodes(
  episodes: readonly NeedsReplySource[],
): readonly NeedsReplyEpisode[] {
  return episodes
    .filter((ep) => isNeedsReplyStatus(ep.status))
    .map((ep) => ({
      id: ep.id,
      title: ep.title,
      activityAt: ep.date ?? ep.last_message_at ?? ep.updated_at,
    }))
    .sort((a, b) => new Date(b.activityAt).getTime() - new Date(a.activityAt).getTime());
}


export function buildEpisodesTimeline(data: AgentModuleData): readonly TimelineItem[] {
  // @tested-by: tst_fe_unit_agent_007, tst_fe_unit_agent_008
  // @invariant: archived and completed chats stay out of the normal Chats list.
  return buildTimelineFromChats(data.episodes ?? data.chats)
    .filter((item) => item.status !== "completed" && item.status !== "archived");
}

export function getTimelineItemById(
  timeline: readonly TimelineItem[],
  id: string,
): TimelineItem | undefined {
  if (timeline.length === 0) return undefined;
  return timeline.find((item) => item.id === id) ?? timeline[0];
}

// ─── Context Panel ──────────────────────────────────────────────────

export function buildContextPanelProps(
  item: TimelineItem,
  linkedEntities?: readonly LinkedEntitySummary[],
): Omit<ContextPanelProps, "runtime" | "onEntityClick"> {
  const contextEntity = linkedEntities?.find((e) => e.link_kind === "started_with");
  const replyToLinked = linkedEntities?.find((e) => e.link_kind === "reply_to");
  const otherEntities = linkedEntities?.filter(
    (e) => e.link_kind !== "started_with" && e.link_kind !== "reply_to",
  ) ?? [];

  return {
    primaryEntity: contextEntity
      ? {
          id: contextEntity.id,
          schemaId: contextEntity.schema_id,
          name: contextEntity.name ?? item.title,
          createdAt: contextEntity.created_at,
          data: contextEntity.data,
        }
      : undefined,
    replyToEntity: replyToLinked
      ? {
          id: replyToLinked.id,
          schemaId: replyToLinked.schema_id,
          name: replyToLinked.name ?? undefined,
          createdAt: replyToLinked.created_at,
          data: replyToLinked.data,
        }
      : undefined,
    infoItems: [],
    connectedEntities: otherEntities
      .filter((e) => e.id !== contextEntity?.id)
      .map((e) => ({
        id: e.id,
        schemaId: e.schema_id,
        name: e.name ?? undefined,
        linkKind: e.link_kind,
        createdAt: e.created_at,
        data: e.data,
      })),
  };
}
